import Analytics from '@/utils/analytics';
import i18n from '@/utils/i18n';
import NetInfo from '@/adapters/netinfo';
import NoInternetConnectionIcon from '@assets/icons/no-internet-connection.svg';
import React, { useCallback, useEffect, useRef } from 'react';
import { createStyles } from '@/contexts/theme-context';
import {
  NetworkStatus,
  useNetworkStatus,
} from '@/contexts/network-status-context';
import { Text, View } from 'react-native';
import { Type } from '@/components/toast';
import { TYPOGRAPHY } from '@/styles';
import { useNetworkGracePeriod } from './use-network-grace-period';
import { useToasts } from '@/contexts/toast-context';

export const useNetworkErrorToast = () => {
  const { setGraceTimeout, clearGraceTimeout } = useNetworkGracePeriod();
  const toastIDRef = useRef<string | null>(null);
  const { networkStatus } = useNetworkStatus();
  const { showToast, closeToast } = useToasts();
  const [styles, theme] = useStyles();

  const trackNetworkState = useCallback(async () => {
    const networkState = await NetInfo.fetch();
    Analytics.track('network_status_error_toast.open', {
      isConnected: networkState.isConnected,
      isReachable: networkState.isInternetReachable,
      type: networkState.type,
    });
  }, []);

  useEffect(() => {
    if (networkStatus === NetworkStatus.CONNECTED) {
      clearGraceTimeout();
      if (toastIDRef.current) {
        closeToast(toastIDRef.current);
        toastIDRef.current = null;
      }
    } else if (networkStatus === NetworkStatus.DISCONNECTED) {
      setGraceTimeout(() => {
        if (!toastIDRef.current) {
          toastIDRef.current =
            showToast({
              autoCloseTimeout: 0,
              closeButtonStyle: {
                marginLeft: 0, // override default margin
                position: 'absolute',
                right: 16,
                top: 18,
              },
              closeIconColor: theme.colors.primary,
              containerStyle: styles.toastContainer,
              content: (
                <View
                  style={styles.toastContentStyle}
                  testID="network-error-toast"
                >
                  <NoInternetConnectionIcon height={40} width={40} />
                  <View collapsable={false}>
                    <Text
                      style={[TYPOGRAPHY.pSemiBold, styles.toastHeaderText]}
                    >
                      {i18n.t('no_internet_connection')}
                    </Text>
                    <Text style={[TYPOGRAPHY.p, styles.toastDescriptionText]}>
                      {i18n.t('no_internet_connection.description')}
                    </Text>
                  </View>
                </View>
              ),
              showCloseButton: true,
              type: Type.MINIMAL,
            }) || null;
        }

        trackNetworkState();
      });
    }

    return () => {
      clearGraceTimeout();
    };
  }, [
    closeToast,
    showToast,
    networkStatus,
    trackNetworkState,
    setGraceTimeout,
    clearGraceTimeout,
    theme,
    styles,
  ]);
};

const useStyles = createStyles((theme) => ({
  toastContainer: {
    backgroundColor: theme.colors.white,
    gap: 12,
    maxWidth: 400,
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  toastContentStyle: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 12,
  },
  toastDescriptionText: {
    color: theme.colors.inactiveText,
    fontSize: 14,
  },
  toastHeaderText: {
    fontSize: 16,
  },
}));
